import React, { useState } from 'react';
import { Apple, Loader2, MessageCircle, ChevronRight, AlertTriangle, X, PoundSterling, Dumbbell } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const COMMON_ALLERGIES = [
  'Dairy', 'Gluten', 'Nuts', 'Eggs', 'Soy', 'Shellfish', 'Fish', 'Wheat', 'Sesame', 'Lactose'
];

const NutritionGuidePage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [allergies, setAllergies] = useState([]);
  const [customAllergy, setCustomAllergy] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', age: '', gender: '',
    current_weight: '', goal: '', target_weight: '',
    diet_type: 'no_preference',
    meals_per_day: '3',
    cooking_skill: 'intermediate',
    budget: 'moderate',
    dislikes: '',
    typical_day: '',
    gym_membership: 'no',
    monthly_takeaway_spend: '',
    monthly_coffee_spend: '',
    gym_frequency: '0'
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleAllergy = (a) => {
    setAllergies(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  };

  const addCustomAllergy = () => {
    const trimmed = customAllergy.trim();
    if (trimmed && !allergies.includes(trimmed)) {
      setAllergies(prev => [...prev, trimmed]);
    }
    setCustomAllergy('');
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        current_weight: parseFloat(form.current_weight),
        target_weight: form.target_weight ? parseFloat(form.target_weight) : null,
        meals_per_day: parseInt(form.meals_per_day),
        monthly_takeaway_spend: form.monthly_takeaway_spend ? parseInt(form.monthly_takeaway_spend) : null,
        monthly_coffee_spend: form.monthly_coffee_spend ? parseInt(form.monthly_coffee_spend) : null,
        allergies
      };
      const res = await axios.post(`${API}/api/nutrition-guide`, payload);
      setResult(res.data.guide);
    } catch (e) {
      setResult("Sorry, we couldn't generate your guide right now. Please try again or contact Aaron directly.");
    }
    setLoading(false);
  };

  const canProceed1 = form.name && form.email && form.current_weight && form.goal;
  const canProceed2 = form.typical_day.length > 10;

  // Compute conversion data for the sales card
  const takeawaySpend = parseInt(form.monthly_takeaway_spend) || 0;
  const coffeeSpend = parseInt(form.monthly_coffee_spend) || 0;
  const totalWaste = takeawaySpend + coffeeSpend;

  return (
    <div className="nutri-page" data-testid="nutrition-page">
      <div className="nutri-container">

        <div className="nutri-header">
          <Apple size={36} className="nutri-header-icon" />
          <h1>Your Free Personalised Nutrition Guide</h1>
          <p>Get a tailored meal plan and nutrition strategy built around your goals, preferences and lifestyle. Takes 2 minutes.</p>
          <span className="nutri-free-badge">100% Free — No Strings Attached</span>
        </div>

        {!result && !loading && (
          <>
            <div className="assess-progress">
              <div className="assess-progress-bar" style={{ width: step === 1 ? '25%' : step === 2 ? '50%' : step === 3 ? '75%' : '100%' }} />
            </div>

            {/* Step 1: Basics & Goal */}
            {step === 1 && (
              <div className="nutri-step" data-testid="nutri-step-1">
                <div className="nutri-step-header">
                  <span className="nutri-step-num">1</span>
                  <div>
                    <h2>Your Details & Goal</h2>
                    <p>This helps us calculate your nutritional needs accurately.</p>
                  </div>
                </div>

                <div className="assess-form-grid">
                  <div className="assess-field">
                    <label>First Name *</label>
                    <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Sarah" data-testid="nutri-name" />
                  </div>
                  <div className="assess-field">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@email.com" data-testid="nutri-email" />
                  </div>
                  <div className="assess-field">
                    <label>Age</label>
                    <input type="number" value={form.age} onChange={e => set('age', e.target.value)} placeholder="e.g. 30" />
                  </div>
                  <div className="assess-field">
                    <label>Gender</label>
                    <select value={form.gender} onChange={e => set('gender', e.target.value)}>
                      <option value="">Prefer not to say</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="assess-field">
                    <label>Current Weight (kg) *</label>
                    <input type="number" step="0.1" value={form.current_weight} onChange={e => set('current_weight', e.target.value)} placeholder="e.g. 78" data-testid="nutri-weight" />
                  </div>
                  <div className="assess-field">
                    <label>Target Weight (kg)</label>
                    <input type="number" step="0.1" value={form.target_weight} onChange={e => set('target_weight', e.target.value)} placeholder="e.g. 70" />
                  </div>
                </div>

                <div className="assess-field assess-field-full">
                  <label>What's your main goal? *</label>
                  <div className="assess-goal-grid" data-testid="nutri-goal">
                    {[
                      { value: 'lose_weight', label: 'Lose Weight', desc: 'Drop body fat sustainably' },
                      { value: 'build_muscle', label: 'Build Muscle', desc: 'Gain lean mass and strength' },
                      { value: 'tone_up', label: 'Tone Up', desc: 'Get leaner and more defined' },
                      { value: 'eat_healthier', label: 'Eat Healthier', desc: 'Better energy and wellbeing' }
                    ].map(g => (
                      <button key={g.value} className={`assess-goal-card ${form.goal === g.value ? 'active' : ''}`} onClick={() => set('goal', g.value)}>
                        <strong>{g.label}</strong>
                        <span>{g.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button onClick={() => setStep(2)} disabled={!canProceed1} className="assess-next-btn" data-testid="nutri-next-1">
                  Next: Your Eating Habits <ChevronRight size={18} />
                </Button>
              </div>
            )}

            {/* Step 2: Eating Habits & Lifestyle Spend */}
            {step === 2 && (
              <div className="nutri-step" data-testid="nutri-step-2">
                <div className="nutri-step-header">
                  <span className="nutri-step-num">2</span>
                  <div>
                    <h2>Your Current Habits & Lifestyle</h2>
                    <p>Be honest — this ensures your plan is realistic and something you'll actually stick to.</p>
                  </div>
                </div>

                <div className="assess-field assess-field-full">
                  <label>Describe a typical day of eating for you *</label>
                  <textarea value={form.typical_day} onChange={e => set('typical_day', e.target.value)} rows={4}
                    placeholder="e.g. I usually skip breakfast, have a sandwich or meal deal for lunch, then something like pasta or a takeaway for dinner. I snack on crisps or chocolate in the afternoon..."
                    data-testid="nutri-typical-day" />
                </div>

                <div className="assess-form-grid">
                  <div className="assess-field">
                    <label>Diet Preference</label>
                    <select value={form.diet_type} onChange={e => set('diet_type', e.target.value)}>
                      <option value="no_preference">No preference</option>
                      <option value="vegetarian">Vegetarian</option>
                      <option value="vegan">Vegan</option>
                      <option value="pescatarian">Pescatarian</option>
                      <option value="halal">Halal</option>
                      <option value="keto">Keto</option>
                    </select>
                  </div>
                  <div className="assess-field">
                    <label>Meals per day</label>
                    <select value={form.meals_per_day} onChange={e => set('meals_per_day', e.target.value)}>
                      <option value="2">2 meals</option>
                      <option value="3">3 meals</option>
                      <option value="4">3 meals + snacks</option>
                      <option value="5">4-5 smaller meals</option>
                    </select>
                  </div>
                  <div className="assess-field">
                    <label>Cooking confidence</label>
                    <select value={form.cooking_skill} onChange={e => set('cooking_skill', e.target.value)}>
                      <option value="beginner">Beginner — keep it simple</option>
                      <option value="intermediate">Intermediate — happy to cook</option>
                      <option value="advanced">Advanced — love cooking</option>
                    </select>
                  </div>
                  <div className="assess-field">
                    <label>Weekly food budget</label>
                    <select value={form.budget} onChange={e => set('budget', e.target.value)}>
                      <option value="budget">Budget-friendly</option>
                      <option value="moderate">Moderate</option>
                      <option value="flexible">Flexible / no limit</option>
                    </select>
                  </div>
                </div>

                <div className="nutri-spend-section">
                  <h4 className="nutri-spend-title"><PoundSterling size={16} /> Quick spend check <span className="nutri-spend-hint">(helps us put things in perspective)</span></h4>
                  <div className="assess-form-grid">
                    <div className="assess-field">
                      <label>Do you have a gym membership?</label>
                      <select value={form.gym_membership} onChange={e => set('gym_membership', e.target.value)}>
                        <option value="no">No</option>
                        <option value="yes_dont_go">Yes, but I barely go</option>
                        <option value="yes_sometimes">Yes, I go sometimes</option>
                        <option value="yes_regular">Yes, I go regularly</option>
                      </select>
                    </div>
                    <div className="assess-field">
                      <label>How many times a week do you train?</label>
                      <select value={form.gym_frequency} onChange={e => set('gym_frequency', e.target.value)}>
                        <option value="0">I don't currently</option>
                        <option value="1">Once a week</option>
                        <option value="2">Twice a week</option>
                        <option value="3">3 times a week</option>
                        <option value="4">4+ times a week</option>
                      </select>
                    </div>
                    <div className="assess-field">
                      <label>Roughly how much do you spend on takeaways per month? (£)</label>
                      <input type="number" value={form.monthly_takeaway_spend} onChange={e => set('monthly_takeaway_spend', e.target.value)} placeholder="e.g. 120" />
                    </div>
                    <div className="assess-field">
                      <label>How much on coffees/drinks out per month? (£)</label>
                      <input type="number" value={form.monthly_coffee_spend} onChange={e => set('monthly_coffee_spend', e.target.value)} placeholder="e.g. 50" />
                    </div>
                  </div>
                </div>

                <div className="assess-field assess-field-full">
                  <label>Any foods you really dislike?</label>
                  <input value={form.dislikes} onChange={e => set('dislikes', e.target.value)} placeholder="e.g. mushrooms, liver, cottage cheese..." />
                </div>

                <div className="assess-btn-row">
                  <Button onClick={() => setStep(1)} className="assess-back-btn">Back</Button>
                  <Button onClick={() => setStep(3)} disabled={!canProceed2} className="assess-next-btn" data-testid="nutri-next-2">
                    Next: Allergies & Intolerances <ChevronRight size={18} />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Allergies */}
            {step === 3 && (
              <div className="nutri-step" data-testid="nutri-step-3">
                <div className="nutri-step-header">
                  <AlertTriangle size={22} className="nutri-allergy-icon" />
                  <div>
                    <h2>Allergies & Intolerances</h2>
                    <p>Select any that apply so we can exclude them completely from your plan.</p>
                  </div>
                </div>

                <div className="nutri-allergy-grid">
                  {COMMON_ALLERGIES.map(a => (
                    <button key={a} className={`nutri-allergy-chip ${allergies.includes(a) ? 'active' : ''}`} onClick={() => toggleAllergy(a)} data-testid={`allergy-${a.toLowerCase()}`}>
                      {allergies.includes(a) && <X size={14} />}
                      {a}
                    </button>
                  ))}
                </div>

                <div className="nutri-custom-allergy">
                  <input value={customAllergy} onChange={e => setCustomAllergy(e.target.value)} placeholder="Add other allergy/intolerance..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomAllergy())} />
                  <button onClick={addCustomAllergy} className="portal-btn-sm" type="button">Add</button>
                </div>

                {allergies.length > 0 && (
                  <div className="nutri-selected-allergies">
                    <span className="nutri-selected-label">Excluding:</span>
                    {allergies.map(a => (
                      <span key={a} className="nutri-selected-chip">
                        {a} <button onClick={() => toggleAllergy(a)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="assess-btn-row" style={{ marginTop: '1.5rem' }}>
                  <Button onClick={() => setStep(2)} className="assess-back-btn">Back</Button>
                  <Button onClick={submit} className="assess-next-btn nutri-generate-btn" data-testid="nutri-submit">
                    <Apple size={18} /> Generate My Free Nutrition Guide
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Loading */}
        {loading && (
          <div className="nutri-loading" data-testid="nutri-loading">
            <Loader2 size={44} className="portal-spin" />
            <h3>Creating your personalised nutrition guide...</h3>
            <p>We're building a tailored meal plan based on your goals, preferences and lifestyle. This takes about 15 seconds.</p>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div className="nutri-result" data-testid="nutri-result">
            <div className="nutri-result-card">
              <div className="nutri-result-badge">Your Personalised Guide</div>
              <h2>{form.name}'s Nutrition Plan</h2>
              <div className="nutri-result-body">
                {result.split('\n').map((line, i) => {
                  if (!line.trim()) return null;
                  if (line.startsWith('## ')) return <h2 key={i} className="nutri-result-h2">{line.replace('## ', '')}</h2>;
                  if (line.startsWith('### ') || (line.startsWith('**') && line.endsWith('**') && line.length < 80)) return <h3 key={i} className="nutri-result-heading">{line.replace(/^###\s?/, '').replace(/\*\*/g, '')}</h3>;
                  if (line.startsWith('- ') || line.startsWith('* ')) {
                    const text = line.replace(/^[-*]\s/, '');
                    const parts = text.split('**');
                    return <li key={i} className="nutri-result-bullet">{parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}</li>;
                  }
                  if (line.includes('**')) {
                    const parts = line.split('**');
                    return <p key={i}>{parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}</p>;
                  }
                  return <p key={i}>{line}</p>;
                })}
              </div>
            </div>

            {/* Gated Full Plan CTA */}
            <div className="nutri-gate-card" data-testid="gated-plan-cta">
              <div className="nutri-gate-lock">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <h3>Want the full 7-day plan?</h3>
              <p>You've just seen a taste of what a personalised nutrition approach looks like. The full guide includes:</p>
              <ul className="nutri-gate-list">
                <li>Complete 7-day meal plan with full recipes & portions</li>
                <li>A tailored shopping list to save you time and money</li>
                <li>Macro breakdowns for every single meal</li>
                <li>Weekly meal prep guide so you stay ahead</li>
              </ul>
              <p className="nutri-gate-unlock">Book your <strong>2 introductory sessions (just £50)</strong> and I'll send you the complete guide — plus build your training plan around it so nutrition and exercise work together.</p>
              <a href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%27ve%20done%20the%20nutrition%20guide%20on%20your%20website%20and%20I%27d%20love%20to%20book%20the%20intro%20sessions%20and%20get%20the%20full%207-day%20plan" target="_blank" rel="noopener noreferrer" className="nutri-gate-btn" data-testid="gate-whatsapp-cta">
                <MessageCircle size={20} /> Book Intro Sessions & Get Full Plan
              </a>
            </div>

            {/* Money Reframe Conversion Card */}
            <div className="nutri-money-card" data-testid="money-reframe">
              <div className="nutri-money-icon"><PoundSterling size={28} /></div>
              <h3>Let's be real for a second, {form.name}.</h3>

              {totalWaste > 0 ? (
                <>
                  <p className="nutri-money-stat">
                    Right now you're spending around <strong>£{totalWaste}/month</strong> on
                    {takeawaySpend > 0 && ` takeaways`}
                    {takeawaySpend > 0 && coffeeSpend > 0 && ' and'}
                    {coffeeSpend > 0 && ' coffees'}
                    . That's <strong>£{totalWaste * 12} a year</strong> going towards food and drink that's actively pulling you further from where you want to be.
                  </p>
                  <p>Think about that for a moment. You already know what you want to change — you literally just told me your goals. The nutrition guide above proves there's a better way to eat. The question is: <strong>will you actually follow through on your own?</strong></p>
                  <p>Honestly? Most people won't. Not because they don't want to, but because life gets in the way. That's where having someone in your corner changes everything.</p>
                  <p>For {totalWaste >= 200 ? 'a fraction of' : totalWaste >= 100 ? 'the same money as' : 'less than'} what you're currently spending on food that's holding you back, you could have:</p>
                </>
              ) : (
                <>
                  <p>You've seen what the right nutrition looks like. The question is: <strong>will you follow through on your own?</strong></p>
                  <p>Honestly, most people won't. Not because they lack motivation — but because without structure, accountability, and someone adjusting things as you go, it's too easy to slip back into old habits. That's where having a PT changes everything.</p>
                  <p>For around <strong>£100/month</strong> — less than most people spend on takeaways — you could have:</p>
                </>
              )}

              <ul className="nutri-money-benefits">
                <li><Dumbbell size={16} /> A proper training plan designed specifically for your body and your goal</li>
                <li><Dumbbell size={16} /> Someone keeping you accountable — so you don't quit after 2 weeks like last time</li>
                <li><Dumbbell size={16} /> Your nutrition adjusted in real-time as your body changes and responds</li>
                <li><Dumbbell size={16} /> Expert guidance that turns this guide from "nice to read" into actual, visible results</li>
              </ul>

              {form.gym_membership === 'yes_dont_go' && (
                <p className="nutri-money-nudge">You told me you have a gym membership you barely use. You're already paying for the tools — you just need someone to show you how to use them properly. That's literally what I do.</p>
              )}
              {form.gym_membership === 'no' && form.gym_frequency === '0' && (
                <p className="nutri-money-nudge">You're not training at all right now. This nutrition plan alone will help — but the real transformation happens when training and nutrition work together. Even just 2 sessions a month would accelerate your results dramatically.</p>
              )}
              {form.gym_membership === 'yes_sometimes' && (
                <p className="nutri-money-nudge">You're already going to the gym sometimes — which means you have the willingness. What's missing is the structure and accountability to make those sessions actually count. That's the difference a PT makes.</p>
              )}

              <div className="nutri-money-offer">
                <p><strong>Start with 2 intro sessions for just £50.</strong> That's {takeawaySpend > 50 ? `less than you spend on takeaways in a single week` : `less than a night out`}. If it doesn't feel right, no pressure — but I think you'll see why hundreds of clients trust me with their goals.</p>
              </div>
            </div>

            {/* Email + Final CTA */}
            <div className="nutri-email-cta" data-testid="email-cta">
              <h3>Get this guide sent to your inbox</h3>
              <p>We'll email your personalised nutrition plan to <strong>{form.email}</strong> so you can reference it anytime.</p>
              <div className="nutri-email-row">
                <a href={`mailto:${form.email}?subject=Your Personalised Nutrition Guide from Aaron Choi&body=Hi ${form.name},%0A%0AThanks for completing the nutrition assessment! To get your full personalised guide and start your transformation, book your intro sessions here:%0A%0Ahttps://wa.me/447787166065%0A%0ABest,%0AAaron Choi%0AAC Wellness %26 Development`} className="nutri-email-btn" data-testid="email-guide-btn">
                  Send Guide to My Email
                </a>
              </div>
              <p className="nutri-email-note">Prefer to chat first? No problem at all.</p>
            </div>

            {/* Final Conversion CTA */}
            <div className="nutri-final-cta" data-testid="final-cta">
              <h3>Here's what happens next if you're ready</h3>
              <div className="nutri-steps-list">
                <div className="nutri-step-item">
                  <span className="nutri-step-circle">1</span>
                  <div>
                    <strong>Message me on WhatsApp</strong>
                    <p>Tell me you did the nutrition assessment — I'll already know your goals</p>
                  </div>
                </div>
                <div className="nutri-step-item">
                  <span className="nutri-step-circle">2</span>
                  <div>
                    <strong>We book your 2 intro sessions (£50)</strong>
                    <p>No commitment beyond that — just a chance to see if we're the right fit</p>
                  </div>
                </div>
                <div className="nutri-step-item">
                  <span className="nutri-step-circle">3</span>
                  <div>
                    <strong>I send you the full 7-day nutrition plan + your training programme</strong>
                    <p>Everything tailored to you, your body, and your goals</p>
                  </div>
                </div>
              </div>
              <a href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%27ve%20just%20done%20the%20nutrition%20guide%20on%20your%20website.%20I%27d%20love%20to%20book%20the%20intro%20sessions%20and%20get%20started" target="_blank" rel="noopener noreferrer" className="assess-whatsapp-btn nutri-final-whatsapp" data-testid="nutri-final-whatsapp">
                <MessageCircle size={22} /> Let's Get Started — Message Aaron
              </a>
              <Button onClick={() => { setResult(null); setStep(1); setAllergies([]); setForm({ name: '', email: '', age: '', gender: '', current_weight: '', goal: '', target_weight: '', diet_type: 'no_preference', meals_per_day: '3', cooking_skill: 'intermediate', budget: 'moderate', dislikes: '', typical_day: '', gym_membership: 'no', monthly_takeaway_spend: '', monthly_coffee_spend: '', gym_frequency: '0' }); }} className="assess-back-btn" style={{ marginTop: '1rem', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}>
                Generate Another Guide
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NutritionGuidePage;
